// ABOUTME: Main orchestrator for AdminFamilyDetail - manages state, API calls, coordinates sections/modals
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import { getFamilyById, getFamilySubscriptionStatus, getFamilyBehaviors, roleAPI, getUserDetail, familyAPI, goalsAPI, getFamilyAnalytics, getBehaviorPatterns, getCurrentAccessToken } from '../../services/api';
import api from '../../services/api';
import { RoleSuggestion, FamilyDeletionSummary } from '../../types/api';
import { parseISO, formatDistanceToNow } from 'date-fns';
import { monthNameToNumber } from '../../utils/dateUtils';
import { BehaviorEventEntry } from '../BehaviorEventsTable';
import {
  parseBehaviorType,
  behaviorTypeToApiString,
  BehaviorFilterState,
  getDefaultBehaviorFilterState
} from '../../utils/behaviorUtils';
import type { HourlyBehaviorAverage } from '../../types/analytics';

// Import types from our types file
import type {
  FamilyMember,
  PendingInvitation,
  FamilyDetailData,
  SubscriptionInfo,
  FamilyAnalytics,
  AddAdultFormState,
  AddChildFormState,
  EditFamilyFormState,
  DeleteFormState,
} from './types';
import FamilyHeaderSection from './FamilyHeaderSection';
import FamilyInfoCardsSection from './FamilyInfoCardsSection';
import AdultsSection from './AdultsSection';
import PendingInvitationsSection from './PendingInvitationsSection';
import ChildrenSection from './ChildrenSection';
import FamilyGoalsSection from './FamilyGoalsSection';
import FamilyAnalyticsSection from './FamilyAnalyticsSection';
import FamilyBehaviorLogSection from './FamilyBehaviorLogSection';
import ModalsContainer from './ModalsContainer';

// Shape of the behavior-patterns-by-time response used to feed the analytics section's TimePatternsChart
interface BehaviorPatternsData {
  hourlyAverages?: HourlyBehaviorAverage[];
}

// Minimal shape read from GET /api/admin/users/{id} for populating each member's custom role
interface UserRoleDetail {
  role?: string;
}

// Raw behavior record shape returned by GET /api/admin/families/{id}/behaviors, mapped into BehaviorEventEntry
interface AdminBehaviorRecord {
  id: number;
  childId?: number;
  childName: string;
  behaviorType: string | number;
  description: string;
  timestamp: string;
  eventTimezone?: string;
  eventLocalTime?: string;
  recordedByName?: string;
  recordedByUserId: number;
  sourceDisplayName?: string;
  source?: number;
}

const AdminFamilyDetail: React.FC = () => {
  const navigate = useNavigate();
  const { familyId } = useParams<{ familyId: string }>();

  // Main state
  const [family, setFamily] = useState<FamilyDetailData | null>(null);
  const [behaviors, setBehaviors] = useState<BehaviorEventEntry[]>([]);
  const [analytics, setAnalytics] = useState<FamilyAnalytics | null>(null);
  const [behaviorPatterns, setBehaviorPatterns] = useState<BehaviorPatternsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [behaviorLoading, setBehaviorLoading] = useState(false);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);

  // Edit family dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState<EditFamilyFormState>({
    name: '',
    description: '',
    timezone: '',
    themeName: 'default'
  });

  // Behavior pagination states
  const [behaviorPage, setBehaviorPage] = useState(1);
  const [behaviorTotalCount, setBehaviorTotalCount] = useState(0);
  const [behaviorPageSize, setBehaviorPageSize] = useState(25);

  // Edit behavior dialog states
  const [editBehaviorDialogOpen, setEditBehaviorDialogOpen] = useState(false);
  const [selectedBehavior, setSelectedBehavior] = useState<BehaviorEventEntry | null>(null);

  // Role edit states
  const [roleEditModalOpen, setRoleEditModalOpen] = useState(false);
  const [editingMemberRole, setEditingMemberRole] = useState<{userId: number, currentRole: string, familyId: number} | null>(null);
  const [memberCustomRoles, setMemberCustomRoles] = useState<{[userId: number]: string}>({});

  // Behavior filters
  const [behaviorFilters, setBehaviorFilters] = useState<BehaviorFilterState>(getDefaultBehaviorFilterState());

  // Pending invitations
  const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Remove family member states
  const [removeMemberDialogOpen, setRemoveMemberDialogOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<FamilyMember | null>(null);

  // Add family member modal states
  const [addAdultModalOpen, setAddAdultModalOpen] = useState(false);
  const [addChildModalOpen, setAddChildModalOpen] = useState(false);
  const [roleSuggestions, setRoleSuggestions] = useState<RoleSuggestion[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [addFamilyMemberError, setAddFamilyMemberError] = useState<string | null>(null);

  // Add adult form state
  const [addAdultForm, setAddAdultForm] = useState<AddAdultFormState>({
    firstName: '',
    lastName: '',
    email: '',
    role: 'Parent',
    isPrimaryCaregiver: false,
    invitationMethod: 'email',
    password: '',
    confirmPassword: '',
    mantra: '',
    familyLoveQuestion: '',
    familyNeedQuestion: ''
  });

  const [showPasswords, setShowPasswords] = useState({
    password: false,
    confirmPassword: false
  });

  // Add child form state
  const [addChildForm, setAddChildForm] = useState<AddChildFormState>({
    name: '',
    aliases: [],
    birthYear: 0,
    birthMonth: '',
    gender: '',
    mantra: '',
    familyLoveQuestion: '',
    familyNeedQuestion: ''
  });

  // Admin goal creation modal state
  const [adminGoalModalOpen, setAdminGoalModalOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  // Goal recalculation state
  const [recalculationLoading, setRecalculationLoading] = useState(false);

  // Family deletion state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteForm, setDeleteForm] = useState<DeleteFormState>({
    deleteType: 'soft',
    confirmationText: ''
  });
  // deleteLoading is tracked for a future disabled/spinner state on the delete button but isn't read yet
  const [, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deletionSummary, setDeletionSummary] = useState<FamilyDeletionSummary | null>(null);

  // Subscription state
  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  // Manage subscription modal state
  const [manageSubscriptionModalOpen, setManageSubscriptionModalOpen] = useState(false);

  // Fetch functions
  const fetchSubscriptionInfo = useCallback(async () => {
    if (!familyId) return;

    try {
      setSubscriptionLoading(true);
      const data = await getFamilySubscriptionStatus(parseInt(familyId)) as SubscriptionInfo;
      setSubscriptionInfo(data);
    } catch (err) {
      console.error('Error fetching subscription info:', err);
    } finally {
      setSubscriptionLoading(false);
    }
  }, [familyId]);

  const fetchFamilyDetail = useCallback(async () => {
    if (!familyId) return;

    try {
      setLoading(true);
      setError(null);

      const familyData = await getFamilyById(parseInt(familyId)) as FamilyDetailData;
      setFamily(familyData);
      setEditForm({
        name: familyData.name,
        description: familyData.description || '',
        timezone: familyData.timezone || '',
        themeName: familyData.themeName || 'default'
      });

      // Fetch custom roles for all family members
      await fetchMemberCustomRoles(familyData.adults);

      // Fetch pending invitations
      await fetchPendingInvitations();

      // Fetch subscription info
      await fetchSubscriptionInfo();
    } catch (err) {
      console.error('Error fetching family detail:', err);
      setError('Error loading family details');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchPendingInvitations intentionally omitted; it is stable and adding it would cause extra fetches
  }, [familyId, fetchSubscriptionInfo]);

  const fetchMemberCustomRoles = async (members: FamilyMember[]) => {
    try {
      const customRoles: {[userId: number]: string} = {};

      await Promise.all(
        members.map(async (member) => {
          try {
            const userDetail = await getUserDetail(member.id) as UserRoleDetail;
            customRoles[member.id] = userDetail.role || 'Not set';
          } catch (err) {
            console.error(`Error fetching role for user ${member.id}:`, err);
            customRoles[member.id] = 'Error loading';
          }
        })
      );

      setMemberCustomRoles(customRoles);
    } catch (err) {
      console.error('Error fetching member custom roles:', err);
    }
  };

  const fetchPendingInvitations = async () => {
    if (!familyId) return;

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5001'}/api/family/${familyId}/invitations`, {
        headers: {
          'Authorization': `Bearer ${getCurrentAccessToken()}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const invitations = await response.json();
        setPendingInvitations(invitations);
      } else {
        console.warn('Failed to load pending invitations');
        setPendingInvitations([]);
      }
    } catch (err) {
      console.warn('Error loading pending invitations:', err);
      setPendingInvitations([]);
    }
  };

  const fetchBehaviors = useCallback(async () => {
    if (!familyId) return;

    try {
      setBehaviorLoading(true);

      const childIdParam = behaviorFilters.selectedChildId;
      const behaviorTypesParam = behaviorFilters.selectedBehaviorTypes.map(type =>
        behaviorTypeToApiString(type)
      );

      const response = await getFamilyBehaviors(parseInt(familyId), behaviorPage, behaviorPageSize, childIdParam, behaviorTypesParam);

      const mappedBehaviors = response.behaviors.map((behavior: AdminBehaviorRecord): BehaviorEventEntry => ({
        behaviorId: behavior.id,
        childId: behavior.childId || 0,
        childName: behavior.childName,
        familyName: family?.name || '',
        behaviorType: parseBehaviorType(behavior.behaviorType),
        notes: behavior.description === 'No description' ? undefined : behavior.description,
        recordedAt: behavior.timestamp,
        eventTime: behavior.timestamp,
        eventTimezone: behavior.eventTimezone,
        eventLocalTime: behavior.eventLocalTime,
        recordedByUserName: behavior.recordedByName || 'Unknown',
        recordedByUserId: behavior.recordedByUserId,
        sourceDisplayName: behavior.sourceDisplayName,
        source: behavior.source?.toString()
      }));

      setBehaviors(mappedBehaviors);
      setBehaviorTotalCount(response.totalCount);
    } catch (err) {
      console.error('Error fetching family behaviors:', err);
    } finally {
      setBehaviorLoading(false);
    }
  }, [familyId, behaviorPage, behaviorPageSize, behaviorFilters, family?.name]);

  const fetchAnalytics = useCallback(async () => {
    if (!familyId) return;

    try {
      setAnalyticsLoading(true);
      setAnalyticsError(null);

      const analyticsData = await getFamilyAnalytics(parseInt(familyId), 30);
      setAnalytics(analyticsData as FamilyAnalytics);
    } catch (err) {
      console.error('Error fetching family analytics:', err);
      setAnalyticsError('Failed to load family analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  }, [familyId]);

  const fetchBehaviorPatterns = useCallback(async () => {
    if (!familyId) return;

    try {
      const patternsData = await getBehaviorPatterns(parseInt(familyId), family?.timezone);
      setBehaviorPatterns(patternsData);
    } catch (err) {
      console.error('Error fetching behavior patterns:', err);
    }
  }, [familyId, family?.timezone]);

  // Effects
  useEffect(() => {
    fetchFamilyDetail();
  }, [fetchFamilyDetail]);

  useEffect(() => {
    if (family) {
      fetchAnalytics();
      fetchBehaviorPatterns();
    }
  }, [fetchAnalytics, fetchBehaviorPatterns, family]);

  useEffect(() => {
    if (family) {
      fetchBehaviors();
    }
  }, [fetchBehaviors, family]);

  // Handler functions
  const handleEditSubmit = async () => {
    if (!familyId || !family) return;

    try {
      await api.put(`/api/admin/families/${familyId}`, editForm);
      setEditDialogOpen(false);
      await fetchFamilyDetail();
    } catch (err) {
      console.error('Error updating family:', err);
    }
  };

  const handleResendInvitation = async (invitationId: number) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5001'}/api/family/invitations/${invitationId}/resend`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getCurrentAccessToken()}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setError(null);
        setSuccessMessage('Invitation resent successfully!');
        await fetchPendingInvitations();

        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        const errorData = await response.json();
        setError(`Failed to resend invitation: ${errorData.message || 'Unknown error'}`);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(`Error resending invitation: ${errorMessage}`);
    }
  };

  const handleCancelInvitation = async (invitationId: number, invitationEmail: string) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5001'}/api/family/invitations/${invitationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getCurrentAccessToken()}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setError(null);
        setSuccessMessage(`Invitation to ${invitationEmail} cancelled successfully!`);
        await fetchPendingInvitations();

        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        const errorData = await response.json();
        setError(`Failed to cancel invitation: ${errorData.message || 'Unknown error'}`);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(`Error cancelling invitation: ${errorMessage}`);
    }
  };

  const handleMemberClick = (memberId: number) => {
    navigate(`/admin/users/${memberId}`);
  };

  const handleChildClick = (childId: number) => {
    navigate(`/admin/children/${childId}`);
  };

  const handleDeleteClick = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5001'}/api/admin/families/${familyId}/subscription-status`, {
        headers: {
          Authorization: `Bearer ${getCurrentAccessToken()}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.hasActiveSubscription) {
          setError('Error: Cannot delete family with an active subscription. Subscription must be cancelled first.');
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
      }
    } catch (err) {
      console.error('Error checking subscription status:', err);
    }

    setDeleteForm({
      deleteType: 'soft',
      confirmationText: ''
    });
    setDeleteError(null);
    setDeletionSummary(null);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDeleteForm({
      deleteType: 'soft',
      confirmationText: ''
    });
    setDeleteError(null);
    setDeletionSummary(null);
  };

  const handleDeleteAcknowledge = () => {
    setDeleteDialogOpen(false);
    setDeleteForm({
      deleteType: 'soft',
      confirmationText: ''
    });
    setDeleteError(null);
    setDeletionSummary(null);
    navigate('/admin/families');
  };

  const handleDeleteSubmit = async () => {
    if (!familyId || !family) return;

    if (deleteForm.confirmationText !== family.name) {
      setDeleteError('Confirmation text must match the family name exactly');
      return;
    }

    try {
      setDeleteLoading(true);
      setDeleteError(null);

      const response = await api.request<{ success: boolean; deletionSummary: FamilyDeletionSummary }>({
        method: 'DELETE',
        url: `/api/admin/families/${familyId}`,
        data: deleteForm
      });

      if (response.data?.success) {
        setDeletionSummary(response.data.deletionSummary);
      }
    } catch (err: unknown) {
      console.error('Error deleting family:', err);
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setDeleteError(axiosErr.response?.data?.message || 'Failed to delete family');
    } finally {
      setDeleteLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Invalid date';
    }
  };

  const formatRelativeTime = (dateString?: string) => {
    if (!dateString) return 'No activity';
    try {
      const utcDate = parseISO(dateString);
      return formatDistanceToNow(utcDate, { addSuffix: true });
    } catch {
      return 'Invalid date';
    }
  };

  const calculateAge = (birthDate: string) => {
    try {
      const birth = new Date(birthDate);
      const today = new Date();
      const age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        return age - 1;
      }
      return age;
    } catch {
      return 'Unknown';
    }
  };

  const handleEditBehavior = (behavior: BehaviorEventEntry) => {
    setSelectedBehavior(behavior);
    setEditBehaviorDialogOpen(true);
  };

  const handleDeleteBehavior = async (_behavior: BehaviorEventEntry) => {
    if (window.confirm('Are you sure you want to delete this behavior record?')) {
      try {
        await fetchBehaviors();
      } catch (err) {
        console.error('Error deleting behavior:', err);
      }
    }
  };

  const canEditDeleteBehavior = (_behavior: BehaviorEventEntry) => {
    return true;
  };

  const handleBehaviorSort = (field: string) => {
    console.log('Sort by:', field);
  };

  const handleBehaviorPageChange = (newPage: number) => {
    setBehaviorPage(newPage + 1);
  };

  const handleBehaviorRowsPerPageChange = (rowsPerPage: number) => {
    setBehaviorPageSize(rowsPerPage);
    setBehaviorPage(1);
  };

  const loadRoleSuggestions = async () => {
    try {
      setLoadingRoles(true);
      const suggestions = await roleAPI.getRoleSuggestions();
      setRoleSuggestions(suggestions);
    } catch (err) {
      console.error('Error loading role suggestions:', err);
      setRoleSuggestions([
        { id: 1, roleName: 'Parent', isActive: true, isPriority: true, displayOrder: 1, createdAt: new Date().toISOString() },
        { id: 2, roleName: 'Guardian', isActive: true, isPriority: true, displayOrder: 2, createdAt: new Date().toISOString() },
        { id: 3, roleName: 'Caregiver', isActive: true, isPriority: true, displayOrder: 3, createdAt: new Date().toISOString() }
      ]);
    } finally {
      setLoadingRoles(false);
    }
  };

  const validateAddAdultForm = (): string | null => {
    if (!addAdultForm.firstName.trim()) return 'First name is required';
    if (!addAdultForm.lastName.trim()) return 'Last name is required';
    if (!addAdultForm.email.trim()) return 'Email is required';
    if (!addAdultForm.role.trim()) return 'Role is required';
    if (addAdultForm.invitationMethod === 'direct') {
      if (!addAdultForm.password.trim()) return 'Password is required for direct addition';
      if (addAdultForm.password.length < 8 || addAdultForm.password.length > 64) {
        return 'Password must be between 8 and 64 characters';
      }
      if (addAdultForm.password !== addAdultForm.confirmPassword) {
        return 'Passwords do not match';
      }
    }
    return null;
  };

  const togglePasswordVisibility = (field: keyof typeof showPasswords) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleAddAdult = async () => {
    if (!family || !familyId) return;

    setAddFamilyMemberError(null);

    const validationError = validateAddAdultForm();
    if (validationError) {
      setAddFamilyMemberError(validationError);
      return;
    }

    try {
      const memberData = {
        FirstName: addAdultForm.firstName,
        LastName: addAdultForm.lastName,
        Email: addAdultForm.email,
        Role: addAdultForm.role === 'Owner' ? 1 : 2,
        CustomRole: addAdultForm.role,
        IsPrimaryCaregiver: addAdultForm.isPrimaryCaregiver,
        InvitationMethod: addAdultForm.invitationMethod,
        ...(addAdultForm.invitationMethod === 'direct' && {
          Password: addAdultForm.password,
          Mantra: addAdultForm.mantra,
          FamilyLoveQuestion: addAdultForm.familyLoveQuestion,
          FamilyNeedQuestion: addAdultForm.familyNeedQuestion
        })
      };

      await api.post(`/api/admin/families/${familyId}/members`, memberData);

      setAddAdultModalOpen(false);
      setAddAdultForm({
        firstName: '',
        lastName: '',
        email: '',
        role: 'Parent',
        isPrimaryCaregiver: false,
        invitationMethod: 'email',
        password: '',
        confirmPassword: '',
        mantra: '',
        familyLoveQuestion: '',
        familyNeedQuestion: ''
      });
      setShowPasswords({ password: false, confirmPassword: false });

      await fetchFamilyDetail();
    } catch (err: unknown) {
      console.error('Error adding family member:', err);
      const axiosErr = err as { response?: { data?: { message?: string } }; message?: string };
      setAddFamilyMemberError(axiosErr.response?.data?.message || axiosErr.message || 'Failed to add family member');
    }
  };

  const handleAddChild = async () => {
    if (!family || !familyId) return;

    try {
      const monthNumber = monthNameToNumber(addChildForm.birthMonth);
      const childData = {
        Name: addChildForm.name,
        Aliases: addChildForm.aliases.length > 0 ? addChildForm.aliases : null,
        BirthYear: addChildForm.birthYear,
        BirthMonth: monthNumber,
        BirthDay: 1,
        Gender: addChildForm.gender || null,
        Mantra: addChildForm.mantra || null,
        FamilyLoveQuestion: addChildForm.familyLoveQuestion || null,
        FamilyNeedQuestion: addChildForm.familyNeedQuestion || null
      };

      await api.post(`/api/admin/families/${familyId}/children`, childData);

      setAddChildModalOpen(false);
      setAddChildForm({
        name: '',
        aliases: [],
        birthYear: 0,
        birthMonth: '',
        gender: '',
        mantra: '',
        familyLoveQuestion: '',
        familyNeedQuestion: ''
      });

      await fetchFamilyDetail();
    } catch (err) {
      console.error('Error adding child:', err);
    }
  };

  const handleRemoveFamilyMember = (member: FamilyMember) => {
    setMemberToRemove(member);
    setRemoveMemberDialogOpen(true);
  };

  const confirmRemoveFamilyMember = async () => {
    if (!memberToRemove || !familyId) return;

    try {
      await familyAPI.removeFamilyMember(parseInt(familyId), memberToRemove.id);

      setRemoveMemberDialogOpen(false);
      setMemberToRemove(null);
      await fetchFamilyDetail();

      setSuccessMessage(`${memberToRemove.firstName} ${memberToRemove.lastName} has been removed from the family.`);
    } catch (err: unknown) {
      console.error('Error removing family member:', err);
      const axiosErr = err as { response?: { data?: { message?: string } }; message?: string };
      alert(axiosErr.response?.data?.message || axiosErr.message || 'Failed to remove family member');
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
  };

  const handleAdminGoalSave = async () => {
    await fetchFamilyDetail();
  };

  const handleRecalculateGoals = async () => {
    if (!family) return;

    setRecalculationLoading(true);
    try {
      const result = await goalsAPI.adminRecalculateFamilyGoals(family.id);

      if (result.success) {
        const message = `Successfully recalculated ${result.totalGoalsRecalculated} goals and fixed ${result.heartsFixed || 0} hearts for ${family.name}. ` +
          `Goal types processed: ${result.goalTypesProcessed?.join(', ') || 'none'}`;
        showSnackbar(message, 'success');
        await fetchFamilyDetail();
      } else {
        showSnackbar(`Goal recalculation failed: ${result.errors?.join(', ') || 'Unknown error'}`, 'error');
        console.error('Recalculation failed:', result);
      }
    } catch (error) {
      console.error('Goal recalculation error:', error);
      showSnackbar('Failed to recalculate goals. Please try again.', 'error');
    } finally {
      setRecalculationLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress data-testid="family-detail-loading" />
      </Box>
    );
  }

  if (error || !family) {
    return (
      <Box p={3}>
        <Alert severity="error">
          {error || 'Family not found'}
        </Alert>
        <Button onClick={() => navigate('/admin/families')} sx={{ mt: 2 }}>
          ← Back to Family Management
        </Button>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header Section */}
      <FamilyHeaderSection
        family={family}
        onBack={() => navigate('/admin/families')}
        onEdit={() => setEditDialogOpen(true)}
        onDelete={handleDeleteClick}
      />

      {/* Success/Error Messages */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      {/* Family Info Cards Section */}
      <FamilyInfoCardsSection
        family={family}
        subscriptionInfo={subscriptionInfo}
        subscriptionLoading={subscriptionLoading}
        formatDate={formatDate}
        formatRelativeTime={formatRelativeTime}
        onManageSubscription={() => setManageSubscriptionModalOpen(true)}
      />

      {/* Adults Section */}
      <AdultsSection
        family={family}
        memberCustomRoles={memberCustomRoles}
        formatRelativeTime={formatRelativeTime}
        onAddAdult={() => {
          setAddAdultForm({
            firstName: '',
            lastName: '',
            email: '',
            role: 'Parent',
            isPrimaryCaregiver: false,
            invitationMethod: 'email',
            password: '',
            confirmPassword: '',
            mantra: '',
            familyLoveQuestion: '',
            familyNeedQuestion: ''
          });
          setShowPasswords({ password: false, confirmPassword: false });
          setAddFamilyMemberError(null);
          setAddAdultModalOpen(true);
          loadRoleSuggestions();
        }}
        onMemberClick={handleMemberClick}
        onEditRole={(userId, currentRole, familyId) => {
          setEditingMemberRole({ userId, currentRole, familyId });
          setRoleEditModalOpen(true);
        }}
        onRemoveMember={handleRemoveFamilyMember}
      />

      {/* Pending Invitations Section */}
      <PendingInvitationsSection
        pendingInvitations={pendingInvitations}
        onResendInvitation={handleResendInvitation}
        onCancelInvitation={handleCancelInvitation}
      />

      {/* Children Section */}
      <ChildrenSection
        family={family}
        calculateAge={calculateAge}
        formatDate={formatDate}
        onAddChild={() => {
          setAddChildForm({
            name: '',
            aliases: [],
            birthYear: 0,
            birthMonth: '',
            gender: '',
            mantra: '',
            familyLoveQuestion: '',
            familyNeedQuestion: ''
          });
          setAddChildModalOpen(true);
        }}
        onChildClick={handleChildClick}
      />

      {/* Family Goals Section */}
      <FamilyGoalsSection
        family={family}
        recalculationLoading={recalculationLoading}
        onAddGoal={() => setAdminGoalModalOpen(true)}
        onRecalculateGoals={handleRecalculateGoals}
      />

      {/* Family Analytics Section */}
      <FamilyAnalyticsSection
        analytics={analytics}
        analyticsLoading={analyticsLoading}
        analyticsError={analyticsError}
        behaviorPatterns={behaviorPatterns}
        family={family}
      />

      {/* Family Behavior Log Section */}
      <FamilyBehaviorLogSection
        family={family}
        behaviors={behaviors}
        behaviorTotalCount={behaviorTotalCount}
        behaviorPage={behaviorPage}
        behaviorPageSize={behaviorPageSize}
        behaviorLoading={behaviorLoading}
        behaviorFilters={behaviorFilters}
        canEditDeleteBehavior={canEditDeleteBehavior}
        onBehaviorFiltersChange={setBehaviorFilters}
        onBehaviorPageChange={handleBehaviorPageChange}
        onBehaviorRowsPerPageChange={handleBehaviorRowsPerPageChange}
        onBehaviorSort={handleBehaviorSort}
        onEditBehavior={handleEditBehavior}
        onDeleteBehavior={handleDeleteBehavior}
        onPageChange={setBehaviorPage}
      />

      {/* Modals */}
      <ModalsContainer
        editDialogOpen={editDialogOpen}
        setEditDialogOpen={setEditDialogOpen}
        editForm={editForm}
        setEditForm={setEditForm}
        handleEditSubmit={handleEditSubmit}
        editBehaviorDialogOpen={editBehaviorDialogOpen}
        setEditBehaviorDialogOpen={setEditBehaviorDialogOpen}
        selectedBehavior={selectedBehavior}
        setSelectedBehavior={setSelectedBehavior}
        fetchBehaviors={fetchBehaviors}
        addAdultModalOpen={addAdultModalOpen}
        setAddAdultModalOpen={setAddAdultModalOpen}
        addAdultForm={addAdultForm}
        setAddAdultForm={setAddAdultForm}
        showPasswords={showPasswords}
        setShowPasswords={setShowPasswords}
        togglePasswordVisibility={togglePasswordVisibility}
        roleSuggestions={roleSuggestions}
        loadingRoles={loadingRoles}
        addFamilyMemberError={addFamilyMemberError}
        handleAddAdult={handleAddAdult}
        addChildModalOpen={addChildModalOpen}
        setAddChildModalOpen={setAddChildModalOpen}
        addChildForm={addChildForm}
        setAddChildForm={setAddChildForm}
        handleAddChild={handleAddChild}
        deleteDialogOpen={deleteDialogOpen}
        handleDeleteCancel={handleDeleteCancel}
        handleDeleteAcknowledge={handleDeleteAcknowledge}
        family={family}
        deleteForm={deleteForm}
        setDeleteForm={setDeleteForm}
        deleteError={deleteError}
        deletionSummary={deletionSummary}
        handleDeleteSubmit={handleDeleteSubmit}
        roleEditModalOpen={roleEditModalOpen}
        setRoleEditModalOpen={setRoleEditModalOpen}
        editingMemberRole={editingMemberRole}
        setEditingMemberRole={setEditingMemberRole}
        fetchFamilyDetail={fetchFamilyDetail}
        setError={setError}
        adminGoalModalOpen={adminGoalModalOpen}
        setAdminGoalModalOpen={setAdminGoalModalOpen}
        handleAdminGoalSave={handleAdminGoalSave}
        showSnackbar={showSnackbar}
        removeMemberDialogOpen={removeMemberDialogOpen}
        setRemoveMemberDialogOpen={setRemoveMemberDialogOpen}
        memberToRemove={memberToRemove}
        confirmRemoveFamilyMember={confirmRemoveFamilyMember}
        manageSubscriptionModalOpen={manageSubscriptionModalOpen}
        setManageSubscriptionModalOpen={setManageSubscriptionModalOpen}
        subscriptionInfo={subscriptionInfo}
        onSubscriptionChange={fetchSubscriptionInfo}
      />

      {/* Snackbar for goal recalculation and other admin action feedback */}
      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={6000}
        onClose={() => setSnackbarMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbarMessage('')} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminFamilyDetail;
